import { FC, useId } from "react";

import { Button, Icon, TooltipStyle } from "../../general";

import { createTokenStyleHook } from "../../tokens";
import { Subheading } from "../../typography";

import { ChatHeader } from "./components/ChatHeader";
import { ConversationContent } from "./components/ConversationContent";
import { PromptFooter } from "./components/PromptFooter";
import { UserInput } from "./components/UserInput";
import { useAgentChatbotController } from "./hooks/useAgentChatbotController";
import { resolveDockStyle } from "./cornerDock";
import { AgentChatbotProps } from "./types";
import tokenStyles from "./styles.module.css";

export type { AgentChatAttachment, AgentChatMessage, AgentChatSubmission, AgentChatbotProps } from "./types";

const useStyles = createTokenStyleHook<AgentChatbotProps>({
  tokenStyles,
  componentTokens: "agent_chatbot",
});

export const AgentChatbot: FC<AgentChatbotProps> = (props) => {
  const {
    open,
    onClose,
    onOpen,
    layout = "standalone",
    dock,
    reservedBottomSpace,
    title = "Ask Judi",
    introTitle = "How can I assist you?",
    introSubtitle = "Not sure where to start? Try asking me…",
    introPresets,
    onPresetSelect,
    onSuggestionSelect,
    messages,
    isLoading = false,
    hasError = false,
    errorMessage = "Unable to connect to Judi's AI service",
    onRetry,
    attachedFiles,
    onRemoveFile,
    onAttachFile,
    onFilesSelected,
    inputValue,
    onInputChange,
    onSubmit,
    onFileDrop,
    isUploadTargetVisible = false,
    footnote,
  } = props;

  const { mode, styles, StyleModeBoundary, cx } = useStyles(props);

  const panelTitleId = useId();

  const {
    currentMessages,
    currentAttachedFiles,
    currentInputValue,
    hasMessages,
    canSubmit,
    loadingNotice,
    scrollRegionRef,
    isScrolledUp,
    handleScroll,
    scrollToLatest,
    panelRef,
    panelHeight,
    minHeight,
    onGrabberPointerDown,
    onGrabberKeyDown,
    textareaRef,
    handleTextareaChange,
    handleTextareaKeyDown,
    handleSubmit,
    handleFilesSelected,
    handleFileDrop,
    handleRemoveFile,
    populateInput,
  } = useAgentChatbotController({
    messages,
    isLoading,
    hasError,
    attachedFiles,
    onFilesSelected,
    onFileDrop,
    onRemoveFile,
    inputValue,
    onInputChange,
    onSubmit,
  });

  const handleClose = () => onClose?.();

  const handlePresetSelect = (preset: string) => {
    populateInput(preset);
    onPresetSelect?.(preset);
  };

  const handleSuggestionSelect = (suggestion: string) => {
    populateInput(suggestion);
    onSuggestionSelect?.(suggestion);
  };

  const renderPromptTextbox = () => (
    <UserInput
      styles={styles}
      textareaRef={textareaRef}
      currentInputValue={currentInputValue}
      canSubmit={canSubmit}
      attachedFiles={currentAttachedFiles}
      loadingNotice={loadingNotice}
      isUploadTargetVisible={isUploadTargetVisible ?? false}
      onAttachFile={onAttachFile || undefined}
      onFilesSelected={handleFilesSelected}
      onRemoveFile={handleRemoveFile}
      onFileDrop={handleFileDrop}
      onTextareaChange={handleTextareaChange}
      onTextareaKeyDown={handleTextareaKeyDown}
      onSubmit={handleSubmit}
      hasError={hasError ?? false}
      onRetry={onRetry || undefined}
    />
  );

  const renderFooter = () => (
    <PromptFooter
      styles={styles}
      cx={cx}
      hasError={Boolean(hasError)}
      errorMessage={errorMessage}
    >
      {renderPromptTextbox()}
    </PromptFooter>
  );

  const renderBody = () => (
    <div className={styles.AgentChatbotContent}>
      <div
        ref={scrollRegionRef}
        className={styles.AgentChatbotScrollRegion}
        onScroll={handleScroll}
      >
        <section className={styles.AgentChatbotConversation}>
          <ConversationContent
            styles={styles}
            cx={cx}
            hasMessages={hasMessages}
            currentMessages={currentMessages}
            isLoading={Boolean(isLoading)}
            introTitle={introTitle}
            introSubtitle={introSubtitle}
            introPresets={introPresets || undefined}
            currentInputValue={currentInputValue}
            onPresetSelect={handlePresetSelect}
            onSuggestionSelect={handleSuggestionSelect}
          />
        </section>
      </div>
      {!!(isScrolledUp && hasMessages) && (
        <button
          type="button"
          className={styles.AgentChatbotScrollToBottom}
          aria-label="Scroll to latest message"
          onClick={scrollToLatest}
          data-testid="scroll-to-bottom"
        >
          <Icon name="chevron-down" className={styles.AgentChatbotScrollToBottomIcon} aria-hidden="true" />
        </button>
      )}
    </div>
  );

  const renderHeaderContent = () => {
    if (layout === "standalone") {
      return (
        <ChatHeader
          styles={styles}
          title={title}
          titleId={panelTitleId}
          onClose={handleClose}
        />
      );
    }

    return <Subheading className={styles.AgentChatbotHeaderTitle} id={panelTitleId}>{title}</Subheading>;
  };

  /* Panel chrome shared by both layouts: the resize grabber, header, conversation body, input
     footer, and optional footnote. */
  const renderPanelContent = () => (
    <TooltipStyle alignment="top">
      <div
        className={styles.AgentChatbotGrabberStrip}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize chat panel"
        aria-valuenow={panelHeight || undefined}
        aria-valuemin={minHeight}
        tabIndex={0}
        onPointerDown={onGrabberPointerDown}
        onKeyDown={onGrabberKeyDown}
        data-testid="chatbot-grabber"
      >
        <div className={styles.AgentChatbotGrabber} aria-hidden="true" />
      </div>
      {!!title && (
        <div className={styles.AgentChatbotHeader}>
          {renderHeaderContent()}
        </div>
      )}
      {renderBody()}
      {renderFooter()}
      {!!footnote && (
        <div className={styles.AgentChatbotFootnote}>
          <p className={styles.AgentChatbotFootnoteText}>{footnote}</p>
        </div>
      )}
    </TooltipStyle>
  );

  /* "embedded" renders inline and is always visible; "standalone" is a floating dialog gated by
     `open`. Both share the same panel chrome and resize behavior. */
  const renderPanel = () => {
    const panelStyle = panelHeight !== null ? { height: `${panelHeight}px` } : undefined;
    if (layout !== "standalone") {
      return (
        <section
          ref={panelRef}
          data-layout="embedded"
          className={styles.AgentChatbotPanel}
          style={panelStyle}
          aria-labelledby={panelTitleId}
          data-testid="chatbot-panel"
        >
          {renderPanelContent()}
        </section>
      );
    }

    if (!open) return null;

    return (
      <dialog
        open
        ref={panelRef}
        data-layout="standalone"
        aria-modal="false"
        className={styles.AgentChatbotPanel}
        style={panelStyle}
        aria-labelledby={panelTitleId}
        data-testid="chatbot-panel"
      >
        {renderPanelContent()}
      </dialog>
    );
  };

  /* The shell fills its container by default; when docked, the fixed wrapper (below) supplies the
     bottom-right position and the min-height-protected height. */
  const renderShell = () => (
    <div className={styles.AgentChatbot}>
      {renderPanel()}
      {layout === "standalone" && (
        <Button
          appearance="primary"
          className={styles.AgentChatbotTrigger}
          icon="message-dots"
          aria-label={open ? `Close Chatbot` : `Open Chatbot`}
          onClick={() => (open ? handleClose() : onOpen?.())}
          testId="chatbot-trigger"
        />
      )}
    </div>
  );

  const dockStyle = dock === "bottom-right" && layout === "standalone" ? resolveDockStyle(reservedBottomSpace) : undefined;

  return (
    <StyleModeBoundary mode={mode}>
      {dockStyle ? <div style={dockStyle}>{renderShell()}</div> : renderShell()}
    </StyleModeBoundary>
  );
};
